// lib/koi-pond-v2/shaders/godray.ts
//
// Volumetric-looking god rays via radial march from an above-frame light
// source (uv = (0.5, 1.1), just above the top of the panel). Sparse
// high-contrast mask + 8-step accumulation = the "shaft of light" look
// without needing a separate render pass.
//
// Pulses on a 6-second cycle (sin(t * 2π/6)) — slow enough that the
// pulse reads as ambient breathing, not a flicker.
//
// Density gradient (abs(uv.x − 0.5) * 2.0) = 0 at center, 1 at edges,
// so the godrays cluster in the LEFT and RIGHT gutters of the panel
// rather than uniformly across — matches the brief's "heavy in left/
// right gutters, sparse in center."

import { ShaderMaterial, AdditiveBlending, NormalBlending, Vector2 } from 'three';
import { paletteVec3 } from '../palette';
import { SAFE_ZONE_COMPLEMENT } from '../constants/safeZone';

export const GODRAY_VERT = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const GODRAY_FRAG = `
  precision highp float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uWind;
  uniform vec2 uResolution;

  float hashGR(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }
  float valueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hashGR(i);
    float b = hashGR(i + vec2(1.0, 0.0));
    float c = hashGR(i + vec2(0.0, 1.0));
    float d = hashGR(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  void main() {
    vec2 uv = vUv;
    vec2 lightSrc = vec2(0.5, 1.1); // above-frame source

    // Radial sparse mask — 8 samples toward the light, threshold high
    float mask = 0.0;
    for (int i = 0; i < 8; i++) {
      float t = float(i) / 8.0;
      vec2 samp = mix(uv, lightSrc, t);
      // uTime moved from Y to X: shafts now drift HORIZONTALLY at
      // 0.05 cells/sec (~20s per cell), matching the spec's "shafts
      // shift across the panel over ~20s." Y is purely positional so
      // each shaft holds its vertical extent.
      float windDriftSpeed = 0.05 * (1.0 + 0.25 * uWind);
      vec2 noiseUV = vec2(samp.x * 6.0 + uTime * windDriftSpeed, samp.y * 0.3);
      float n = valueNoise(noiseUV);
      // Per-sample density bias: multiply the noise contribution by
      // a gutter-heavy weight at THIS sample's screen X. Shafts now
      // form ONLY in gutter regions, not just attenuate there. At
      // samp.x = 0.5 (center) dx = 0, so no shaft contribution can
      // accumulate. At samp.x = 0 or 1 (gutter), dx = 1, full
      // contribution.
      float dx = abs(samp.x - 0.5) * 2.0;
      mask += smoothstep(0.55, 0.85, n) * (1.0 - t) * dx;
    }
    // Clamp the accumulated mask to [0, 1] before using it in the
    // color mix. Without this, mask can reach ~4.5 (sum of (1-t)
    // weights across 8 steps when smoothstep is saturating), causing
    // mix(glow, core, mask > 1) to extrapolate beyond core. The
    // extrapolation produces G > R cool-cast at peaks because the
    // G-channel delta (core - glow = +50) and B-channel delta (+57)
    // dominate the small R-channel delta (+12). Pre-fix shaft cores
    // sampled (63, 73, 64) and (57, 62, 53) — both G > R. Post-fix:
    // mask is in [0, 1], mix interpolates strictly between two warm
    // palette colors, R > G > B is preserved at every pixel.
    mask = clamp(mask, 0.0, 1.0);

    // Pulse on 6s — 1.0472 = 2π/6
    float pulse = 0.7 + 0.3 * sin(uTime * 1.0472);

    // Color: lightShaftGlow at edges falls to lightShaftCore at the
    // brightest mask values. mask is now strictly in [0, 1] so mix
    // interpolates, never extrapolates.
    vec3 color = mix(${paletteVec3('lightShaftGlow')}, ${paletteVec3('lightShaftCore')}, mask);
    vec3 bg = ${paletteVec3('deepestWater')};
    // densityX is no longer a separate alpha factor — the per-sample
    // dx weighting inside the loop already concentrates mask in the
    // gutters. Alpha is just mask × pulse × overall-strength.
    //
    // Phase 6 T6.X — zone-graduated alpha attenuation. Matches Path B
    // pattern from waterSurface / pondFloor: full godray contribution
    // above the safe-zone line (top 28% of viewport), zero below — so
    // additive godray peaks don't add brightness on top of caustic
    // peaks behind HeroContent's lower-zone text.
    float screenYNorm = gl_FragCoord.y / uResolution.y;
    float safeZoneEdgeNorm = ${SAFE_ZONE_COMPLEMENT};
    float fadeBandHalfWidth = 0.04;
    float zoneAttenuation = smoothstep(
      safeZoneEdgeNorm - fadeBandHalfWidth,
      safeZoneEdgeNorm + fadeBandHalfWidth,
      screenYNorm
    );
    float alpha = mask * pulse * 0.6 * zoneAttenuation;
    // When the material is opaque (default — style-guide panel), output
    // the blended RGB + alpha=1.0 (mix paints shafts on top of bg). When
    // transparent (scene overlay, Phase 2 Task 6), output color * alpha
    // with alpha as the alpha channel so additive blending adds shaft
    // brightness to the destination without painting bg over the scene.
    #ifdef GODRAY_TRANSPARENT
      gl_FragColor = vec4(color * alpha, alpha);
    #else
      gl_FragColor = vec4(mix(bg, color, alpha), 1.0);
    #endif
  }
`;

export type GodrayMaterialOpts = {
  transparent?: boolean;
};

export function makeGodrayMaterial(opts: GodrayMaterialOpts = {}): {
  material: ShaderMaterial;
  uniforms: {
    uTime: { value: number };
    uWind: { value: number };
    uResolution: { value: Vector2 };
  };
} {
  const initialW =
    typeof window !== 'undefined' ? window.innerWidth * (window.devicePixelRatio || 1) : 1;
  const initialH =
    typeof window !== 'undefined' ? window.innerHeight * (window.devicePixelRatio || 1) : 1;
  const uniforms = {
    uTime: { value: 0 },
    uWind: { value: 0 },
    uResolution: { value: new Vector2(initialW, initialH) },
  };
  // Transparent mode is opt-in (default: false = opaque-on-deepestWater,
  // which is what the /style-guide godray panel uses and was verified
  // through Phase 1 + Phase 2 Task 1/1b). Phase 2 scene Task 6 passes
  // { transparent: true } to get additive-blending behavior for the
  // in-scene overlay.
  const transparent = opts.transparent ?? false;
  const material = new ShaderMaterial({
    vertexShader: GODRAY_VERT,
    fragmentShader: GODRAY_FRAG,
    uniforms,
    transparent,
    blending: transparent ? AdditiveBlending : NormalBlending,
    depthWrite: !transparent,
    defines: transparent ? { GODRAY_TRANSPARENT: '' } : {},
  });
  return { material, uniforms };
}
